import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AuthPageShell } from "./AuthPageShell";
import { Button } from "@/components/ui/Button";
import { useAuth } from "@/lib/auth-context";

export function Signup() {
  const { signup } = useAuth();
  const navigate = useNavigate();

  const [organizationName, setOrganizationName] = useState("");
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    setSubmitting(true);
    try {
      await signup({ email, password, fullName, organizationName });
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "Sign up failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthPageShell
      title="Create your account"
      subtitle="Start your free trial — no card required."
      footer={
        <>
          Already have an account?{" "}
          <Link to="/login" className="font-medium text-brand-700">
            Log in
          </Link>
        </>
      }
    >
      <form className="space-y-4" onSubmit={handleSubmit}>
        <div>
          <label className="text-sm font-medium text-ink" htmlFor="full-name">
            Your name
          </label>
          <input
            id="full-name"
            type="text"
            required
            autoComplete="name"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            className="mt-1 w-full rounded-md border border-line bg-white px-3 py-2 text-sm text-ink"
            placeholder="Jordan Lee"
          />
        </div>
        <div>
          <label className="text-sm font-medium text-ink" htmlFor="business-name">
            Business name
          </label>
          <input
            id="business-name"
            type="text"
            required
            value={organizationName}
            onChange={(e) => setOrganizationName(e.target.value)}
            className="mt-1 w-full rounded-md border border-line bg-white px-3 py-2 text-sm text-ink"
            placeholder="Acme Plumbing"
          />
        </div>
        <div>
          <label className="text-sm font-medium text-ink" htmlFor="signup-email">
            Work email
          </label>
          <input
            id="signup-email"
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-1 w-full rounded-md border border-line bg-white px-3 py-2 text-sm text-ink"
            placeholder="you@company.com"
          />
        </div>
        <div>
          <label className="text-sm font-medium text-ink" htmlFor="signup-password">
            Password
          </label>
          <input
            id="signup-password"
            type="password"
            required
            autoComplete="new-password"
            minLength={10}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-1 w-full rounded-md border border-line bg-white px-3 py-2 text-sm text-ink"
            placeholder="••••••••"
          />
          <p className="mt-1 text-xs text-ink-soft">
            At least 10 characters, with an uppercase letter, a lowercase letter, and a number.
          </p>
        </div>
        {formError && <p className="text-sm text-red-600">{formError}</p>}
        <Button type="submit" className="w-full" disabled={submitting}>
          {submitting ? "Creating account…" : "Create account"}
        </Button>
      </form>
    </AuthPageShell>
  );
}
